import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-mechanics/format";
import type {
  InclineInput,
  InclineResult,
} from "@/lib/tool-kit/physics-mechanics/incline";

export interface InclineStepStrings {
  stepInputs: string;
  stepWeight: string;
  stepComponents: string;
  stepNormal: string;
  stepMaxStatic: string;
  stepPullFree: string;
  stepPullApplied: string;
  stepHolds: string;
  stepSlides: string;
  stepNet: string;
  stepTravel: string;
  directionUp: string;
  directionDown: string;
}

const RAD_TO_DEG = 180 / Math.PI;

/** The numbered working for a block on a ramp, in SI units and degrees. */
export function inclineSteps(
  input: InclineInput,
  result: InclineResult,
  strings: InclineStepStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const degrees = p(input.angle * RAD_TO_DEG);
  const steps = [
    t(strings.stepInputs, {
      m: p(input.mass),
      angle: degrees,
      mus: p(input.staticMu),
      muk: p(input.kineticMu),
      g: p(input.gravity),
    }),
    t(strings.stepWeight, {
      m: p(input.mass),
      g: p(input.gravity),
      w: r(result.weight),
    }),
    t(strings.stepComponents, {
      w: r(result.weight),
      angle: degrees,
      par: r(result.parallel),
      perp: r(result.perpendicular),
    }),
    t(strings.stepNormal, { n: r(result.normal) }),
    t(strings.stepMaxStatic, {
      mus: p(input.staticMu),
      n: r(result.normal),
      fmax: r(result.maxStatic),
    }),
  ];
  if (input.applied === 0) {
    steps.push(t(strings.stepPullFree, { par: r(result.parallel) }));
  } else {
    steps.push(
      t(strings.stepPullApplied, {
        applied: p(input.applied),
        par: r(result.parallel),
        pull: r(result.pull),
      })
    );
  }
  const pull = r(Math.abs(result.pull));
  if (result.motion === "rest") {
    steps.push(t(strings.stepHolds, { pull, fmax: r(result.maxStatic) }));
    return steps;
  }
  const direction =
    result.motion === "up" ? strings.directionUp : strings.directionDown;
  steps.push(
    t(strings.stepSlides, {
      pull,
      fmax: r(result.maxStatic),
      direction,
      muk: p(input.kineticMu),
      n: r(result.normal),
      fk: r(result.kineticFriction),
    })
  );
  steps.push(
    t(strings.stepNet, {
      net: r(Math.abs(result.net)),
      m: p(input.mass),
      a: r(Math.abs(result.acceleration)),
    })
  );
  if (result.travel !== null && input.length !== null) {
    steps.push(
      t(strings.stepTravel, {
        length: p(input.length),
        a: r(Math.abs(result.acceleration)),
        time: r(result.travel.time),
        speed: r(result.travel.speed),
      })
    );
  }
  return steps;
}
